import React, { useState } from 'react';
import { Seo, breadcrumbSchema, webPageSchema } from '../components/Seo';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';
import { CONTACTS, OFFICES } from '../data/content';
import { Mail, Phone, MapPin, ArrowUpRight, Check } from 'lucide-react';
import WhatsAppPicker from '../components/WhatsAppPicker';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { useRecaptchaToken, recaptchaHeaders, RecaptchaActions } from '@/lib/recaptcha';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const executeRecaptcha = useRecaptchaToken();
  const [form, setForm] = useState({ name: '', company: '', email: '', mobile: '', message: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.message.trim() || form.message.trim().length < 5) e.message = 'Please share a bit more';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error('Please fix the highlighted fields.');
      return;
    }
    setLoading(true);
    try {
      const token = await executeRecaptcha(RecaptchaActions.contact);
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        mobile: form.mobile.trim() || null,
        message: form.message.trim(),
        purpose: 'Contact Form',
        source: 'contact-page',
      }, { headers: recaptchaHeaders(token, RecaptchaActions.contact) });
      setSuccess(true);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // CONTACTS is rendered as a list below (multiple support desks).

  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact AdAura Awards · Marketing Mind India"
        description="Contact the AdAura Awards team. Questions about nominations, sponsorship, jury or partnership? Email awards@marketingmind.co or use the contact form."
        path="/contact"
        jsonLd={[
          webPageSchema({ path: '/contact', name: 'Contact', description: "Contact the AdAura Awards team. Questions about nominations, sponsorship, jury or partnership? Email awards@marketingmin" }),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }]),
        ]}
      />
      <Toaster position="top-center" toastOptions={{ style: { background: 'var(--bg-deep)', color: 'var(--ink)', border: '1px solid rgba(184, 136, 74, 0.4)', borderRadius: 0 } }} />
      <PageHeader
        eyebrow="Get In Touch"
        title="Contact Us"
        italicWords={['Us']}
        subtitle="For nominations, jury, speakership or partnership — write in, call, WhatsApp, or drop us a query and we'll be in touch within 48 hours."
      />
      <section className="pb-20 md:pb-24">
        <div className="content-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 max-w-6xl mx-auto">
            {/* LEFT — coordinates */}
            <div className="lg:col-span-5 space-y-6 relative" data-testid="contact-coordinates">
              {CONTACTS.map((contact, idx) => (
                <Reveal key={contact.for} delay={idx * 100} className="relative">
                  <div className="bg-[#2D1810] text-white p-7 md:p-9 border border-[#B8884A]/30 relative" data-testid={`contact-card-${idx}`}>
                    <div className="text-[10px] tracking-[0.35em] uppercase text-[#D9B26A]">{contact.for}</div>
                    <h2 className="font-serif text-2xl md:text-3xl mt-2 leading-tight">{contact.name}</h2>
                    <div className="mt-8 space-y-4">
                      <a href={`tel:${contact.phone.replace(/\s+/g, '')}`} data-testid={`contact-phone-${idx}`} className="group flex items-center gap-3 text-white/85 hover:text-[#D9B26A] transition-colors text-[14.5px]">
                        <Phone className="w-4 h-4 text-[#D9B26A] flex-shrink-0" />
                        <span>{contact.phone}</span>
                        <ArrowUpRight className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                      <a href={`mailto:${contact.email}`} data-testid={`contact-email-${idx}`} className="group flex items-center gap-3 text-white/85 hover:text-[#D9B26A] transition-colors text-[14.5px] break-all">
                        <Mail className="w-4 h-4 text-[#D9B26A] flex-shrink-0" />
                        <span>{contact.email}</span>
                        <ArrowUpRight className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </a>
                      <WhatsAppPicker phone={contact.phone} testId={`contact-whatsapp-${idx}`} />
                    </div>
                  </div>
                </Reveal>
              ))}

              <Reveal delay={CONTACTS.length * 100}>
                <div className="space-y-5">
                  {OFFICES.map((o) => (
                    <div key={o.label + o.city} className="border border-[#B8884A]/35 p-5 md:p-6 bg-white" data-testid={`office-${o.city.toLowerCase()}`}>
                      <div className="flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-[#9F6A2C] mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="text-[10px] tracking-[0.3em] uppercase text-[#9F6A2C]">{o.label} · {o.city}</div>
                          <p className="text-[13.5px] text-[var(--ink)] mt-2 leading-relaxed">{o.address}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            {/* RIGHT — form */}
            <div className="lg:col-span-7" data-testid="contact-form-card">
              <Reveal delay={80}>
                <div className="bg-white border border-[#B8884A]/35 shadow-[0_24px_60px_-30px_rgba(58,36,24,0.25)]">
                  {success ? (
                    <div className="px-6 md:px-9 py-14 text-center" data-testid="contact-form-success">
                      <span className="w-12 h-12 inline-flex items-center justify-center border border-[#9F6A2C] text-[#9F6A2C]"><Check size={20} /></span>
                      <h4 className="font-serif text-[var(--ink)] text-2xl mt-6">Thanks for writing in.</h4>
                      <p className="text-[var(--ink-muted)] text-sm mt-3 max-w-sm mx-auto leading-relaxed">
                        Atul or the team will reply within 48 hours.
                      </p>
                      <button onClick={() => { setSuccess(false); setForm({ name: '', company: '', email: '', mobile: '', message: '' }); }} className="btn-ghost mt-8" data-testid="contact-form-another">Send another</button>
                    </div>
                  ) : (
                    <form onSubmit={submit} noValidate className="px-6 md:px-9 py-8 space-y-5" data-testid="contact-form">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Row label="Your Name *" err={errors.name}>
                          <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} className="field-input" data-testid="contact-input-name" autoComplete="name" />
                        </Row>
                        <Row label="Company / Brand">
                          <input type="text" value={form.company} onChange={(e) => set('company', e.target.value)} className="field-input" data-testid="contact-input-company" />
                        </Row>
                        <Row label="Email *" err={errors.email}>
                          <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="field-input" data-testid="contact-input-email" autoComplete="email" />
                        </Row>
                        <Row label="Mobile Number">
                          <input type="tel" value={form.mobile} onChange={(e) => set('mobile', e.target.value)} className="field-input" data-testid="contact-input-mobile" autoComplete="tel" />
                        </Row>
                      </div>
                      <Row label="Message / Query *" err={errors.message}>
                        <textarea rows={6} value={form.message} onChange={(e) => set('message', e.target.value)} className="field-input resize-y min-h-[150px]" placeholder="Tell us how we can help..." data-testid="contact-input-message" />
                      </Row>
                      <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                        <p className="text-[11px] text-[var(--ink-muted)] leading-relaxed max-w-xs">
                          We&rsquo;ll never share your details. We reply within 48 hours.
                        </p>
                        <button type="submit" disabled={loading} className="btn-solid disabled:opacity-50 disabled:cursor-not-allowed" data-testid="contact-submit">
                          {loading ? 'Sending...' : (<>Send Message <ArrowUpRight size={14} /></>)}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const Row = ({ label, err, children }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.25em] uppercase text-[#6B4F38] font-semibold mb-2">{label}</span>
    {children}
    {err && <span className="block text-[11px] text-[#C2613A] mt-1.5 tracking-wide">{err}</span>}
  </label>
);

export default Contact;
